import { Entity } from '@common/types/Entity';
import { PlusIcon } from '@prophecy/icons';
import { ReactComponent as UploadIcon } from '@prophecy/icons/assets/images/upload_file.svg?react';
import { theme } from '@prophecy/theme';
import { Button, ProgressBar, Stack, Text, Uploader } from '@prophecy/ui-v3';
import React from 'react';
import styled from 'styled-components';

import { ReactComponent as UploadFileIcon } from '../../../../../assets/upload_grey.svg?react';
import { FileFormatLabels, FileFormats } from '../../../config';
import { MAX_ALLOWED_FILES, MAX_FILE_SIZE } from '../../../constants';
import { useAcceptedFormats } from '../../hook';
import { UploadActionType } from '../../types';
import { useImportState } from '../../useImportState';
import { UploadPercentageCard } from './UploadPercentageCard';

const StyledUploader = styled(Uploader)`
  border: 1px dashed ${theme.colors.gray300};
  border-radius: ${theme.radius.x8};
  margin: 0;
  &:hover {
    background: ${theme.colors.gray50};
  }
`;

const StyledButtonUploader = styled(Uploader)`
  margin: 0;
  > div {
    margin: 0;
  }
`;

const StyledProgressBar = styled(ProgressBar)`
  width: 100%;
  z-index: 0;
`;

const StyledBrowserUpload = styled(Uploader)`
  border-top: 1px solid ${() => theme.colors.gray200};
  background: ${theme.colors.gray50};
  border-radius: 0;
  margin: 0;
  &:hover {
    background: ${theme.colors.gray100};
  }
`;

const UploadPercentageProgressBar = () => {
  const {
    transpileGraphState: {
      clientState: {
        upload: { isUploading, uploadAction }
      },
      overallPercentage
    }
  } = useImportState();

  if (!isUploading) {
    return null;
  }

  return (
    <UploadPercentageCard
      percent={overallPercentage}
      name={uploadAction === UploadActionType.upload ? 'Uploading files...' : 'Processing files...'}
      entity={Entity.Transpiler}
      isOverAll={true}
    />
  );
};

export const UploadFile = ({ isButton, isBrowser }: { isButton?: boolean; isBrowser?: boolean }) => {
  const {
    formDataWithActions: {
      formContext: { getValues }
    },
    transpileGraphState: {
      overallPercentage,
      clientState: {
        upload: { isUploading }
      }
    },
    isDeleting,
    transpilerState: { transpilerId },
    browserDataLoading,
    loading,
    uploadMutationState: { uploadFiles }
  } = useImportState();

  const isLoading = loading || browserDataLoading || isUploading;

  const { source } = getValues() as { source: FileFormats };
  const supportedFormatString = useAcceptedFormats(source as FileFormats);

  if (isButton) {
    return (
      <StyledButtonUploader multiple={true} onDrop={uploadFiles}>
        <Button
          icon={<PlusIcon type='default' />}
          disabled={!transpilerId || isDeleting}
          loading={isLoading}
          size='s'
          variant='secondaryGray'
          data-test-id='upload-file-button'>
          <Text level='sm' weight={theme.fontWeight.medium}>
            Upload Files
          </Text>
        </Button>
      </StyledButtonUploader>
    );
  }

  if (isBrowser) {
    return (
      <Stack>
        {(isUploading || !transpilerId) && (
          <StyledProgressBar height='5px' variant={'loading'} value={overallPercentage} width='100%' />
        )}
        <StyledBrowserUpload multiple={true} onDrop={uploadFiles} loading={isLoading}>
          <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center' align='space-between'>
            <UploadIcon />
            <Stack gap={theme.spaces.x8}>
              <Stack direction='horizontal' gap={theme.spaces.x4}>
                <Text level='sm' tone={theme.colors.gray500}>
                  Drag and drop files or
                </Text>
                <Text level='sm' tone={theme.colors.brand700} weight={theme.fontWeight.medium}>
                  Browse
                </Text>
              </Stack>
              <Text level='xs' tone={theme.colors.gray500}>
                Supports: ({supportedFormatString})
              </Text>
            </Stack>
          </Stack>
        </StyledBrowserUpload>
      </Stack>
    );
  }

  if (isUploading) {
    return <UploadPercentageProgressBar />;
  }

  return (
    <StyledUploader
      disabled={!transpilerId || isDeleting}
      loading={isLoading || !transpilerId}
      onDrop={uploadFiles}
      multiple={true}
      maxFiles={MAX_ALLOWED_FILES}
      maxSize={MAX_FILE_SIZE}>
      <Stack gap={theme.spaces.x12} width='100%' alignY='center' padding={theme.spaces.x8}>
        <UploadFileIcon />

        <Stack direction='horizontal' gap={theme.spaces.x2}>
          <Text level='md' weight={theme.fontWeight.medium} tone={theme.colors.brand600}>
            Click to upload
          </Text>
          <Text level='md' tone={theme.colors.gray900} weight={theme.fontWeight.medium}>
            or drag and drop
          </Text>
        </Stack>
        <Text level='sm' tone={theme.colors.gray500}>
          {FileFormatLabels[source]} files only ({supportedFormatString})
        </Text>
      </Stack>
    </StyledUploader>
  );
};
